/**
 * @typedef {object} BreakdownColumn
 * @property {string} key - The key on `byColumn` to read for each row
 * @property {string} label - Header label
 */

/**
 * @typedef {object} BreakdownRow
 * @property {string} CompagnyID
 * @property {string} name - Project name (CommunityName)
 * @property {number} [value] - Single-value contribution (used when `columns` is not provided)
 * @property {Record<string, number>} [byColumn] - Per-column contribution (used when `columns` is provided)
 */

/**
 * Renders a per-project contribution table for use inside an InfoPopover modal.
 * Supports a single-value mode and a multi-column mode (e.g. status breakdown).
 *
 * @param {object} props
 * @param {Array<BreakdownRow>} props.items - One row per project
 * @param {Array<BreakdownColumn>} [props.columns] - When given, render one cell per column instead of a single `value`
 * @param {string} [props.valueLabel] - Header for the single-value column (default "Aantal")
 * @param {(value: number) => string | number} [props.formatValue] - Display formatter for cells; defaults to identity
 * @param {string} [props.emptyLabel] - Shown when items is empty
 */
const ProjectContributionBreakdown = ({
    items,
    columns,
    valueLabel = 'Aantal',
    formatValue = (v) => v,
    emptyLabel = 'Nog geen projecten met data.',
}) => {
    const rows = Array.isArray(items) ? items : []
    if (rows.length === 0) {
        return <p className='portfolio-breakdown-empty'>{emptyLabel}</p>
    }

    const isColumnMode = Array.isArray(columns) && columns.length > 0

    const rowTotal = (row) => {
        if (isColumnMode) {
            return columns.reduce((sum, c) => sum + (row.byColumn?.[c.key] || 0), 0)
        }
        return row.value || 0
    }

    const sortedRows = [...rows].sort((a, b) => rowTotal(b) - rowTotal(a))

    const columnTotals = isColumnMode
        ? columns.reduce((acc, c) => {
              acc[c.key] = sortedRows.reduce((sum, r) => sum + (r.byColumn?.[c.key] || 0), 0)
              return acc
          }, /** @type {Record<string, number>} */ ({}))
        : null

    const grandTotal = sortedRows.reduce((sum, r) => sum + rowTotal(r), 0)

    return (
        <table className='portfolio-breakdown-table'>
            <thead>
                <tr>
                    <th className='portfolio-breakdown-name-cell'>Project</th>
                    {isColumnMode
                        ? columns.map((c) => (
                              <th key={c.key} className='portfolio-breakdown-number-cell'>
                                  {c.label}
                              </th>
                          ))
                        : (
                              <th className='portfolio-breakdown-number-cell'>{valueLabel}</th>
                          )}
                    <th className='portfolio-breakdown-number-cell'>Totaal</th>
                </tr>
            </thead>
            <tbody>
                {sortedRows.map((row) => (
                    <tr key={row.CompagnyID}>
                        <td className='portfolio-breakdown-name-cell'>{row.name || '—'}</td>
                        {isColumnMode
                            ? columns.map((c) => (
                                  <td key={c.key} className='portfolio-breakdown-number-cell'>
                                      {formatValue(row.byColumn?.[c.key] || 0)}
                                  </td>
                              ))
                            : (
                                  <td className='portfolio-breakdown-number-cell'>
                                      {formatValue(row.value || 0)}
                                  </td>
                              )}
                        <td className='portfolio-breakdown-number-cell portfolio-breakdown-total-cell'>
                            {formatValue(rowTotal(row))}
                        </td>
                    </tr>
                ))}
            </tbody>
            <tfoot>
                <tr>
                    <th className='portfolio-breakdown-name-cell'>Totaal</th>
                    {isColumnMode
                        ? columns.map((c) => (
                              <th key={c.key} className='portfolio-breakdown-number-cell'>
                                  {formatValue(columnTotals[c.key] || 0)}
                              </th>
                          ))
                        : (
                              <th className='portfolio-breakdown-number-cell'>
                                  {formatValue(grandTotal)}
                              </th>
                          )}
                    <th className='portfolio-breakdown-number-cell'>{formatValue(grandTotal)}</th>
                </tr>
            </tfoot>
        </table>
    )
}

export default ProjectContributionBreakdown
